// Vite entrypoint for a node-context page: the client-side filter over its list of node types
// (PLAN-marketing-website §5a). Loaded only on those pages.
//
// A FILTER, NOT A SEARCH PAGE, and that was the decision (Gui, 2026-08-07). A /search route would be
// noindex by nature, so it would do nothing for the reason this whole section exists, and with node
// coverage this early it would mostly return nothing, which reads as an empty product. Meanwhile the
// one page where scanning genuinely beats navigating is this one, because `geo` alone is over two
// hundred rows. Hiding non-matching rows is instant, needs no server, no new URL and no empty state.
//
// The full list stays in the server's HTML and every row keeps its text in a data attribute, so a
// crawler and a reader with no JS both get the complete page. This only ever HIDES rows that are
// already there.

const FILTER_SELECTOR = "[data-node-filter]";
const ROW_SELECTOR = "[data-node-row]";
const STATUS_SELECTOR = "[data-node-filter-status]";

// The searchable text is baked server-side (name + context + categories + summary, lowercased) rather
// than read off the DOM: scraping textContent would also match the markup around it, and would make
// what is searchable depend on how the row happens to be laid out.
function rowText(row: HTMLElement): string {
	return row.dataset.nodeSearch ?? "";
}

// Every whitespace-separated term has to match somewhere in the row, so "geo attribute" narrows
// rather than widening. Substring, not fuzzy: a reader typing "box" wants the boxes, and a fuzzy
// match over 220 short names returns most of them.
function matches(text: string, terms: string[]): boolean {
	return terms.every((term) => text.includes(term));
}

function applyFilter(input: HTMLInputElement, rows: HTMLElement[], status: HTMLElement | null): void {
	const terms = input.value.toLowerCase().split(/\s+/).filter(Boolean);

	if (terms.length === 0) {
		for (const row of rows) row.hidden = false;
		if (status) status.textContent = "";
		return;
	}

	let shown = 0;
	for (const row of rows) {
		const hit = matches(rowText(row), terms);
		row.hidden = !hit;
		if (hit) shown += 1;
	}

	// Announced, not silent. A list that empties itself without a word reads as a broken page, and the
	// `role="status"` on this element is what makes a screen reader say so too.
	if (status) {
		status.textContent =
			shown === 0
				? `No node matches “${input.value}”.`
				: `${shown} of ${rows.length} node types match.`;
	}
}

function init(): void {
	const input = document.querySelector<HTMLInputElement>(FILTER_SELECTOR);
	if (!input) return;

	const rows = Array.from(document.querySelectorAll<HTMLElement>(ROW_SELECTOR));
	if (rows.length === 0) return;

	const status = document.querySelector<HTMLElement>(STATUS_SELECTOR);

	// init runs once per page but can be CALLED more than once for the same one (see below), and a
	// second listener on the same box would run the same filter twice per keystroke. The flag lives
	// on the element, so a Turbo body swap throws it away with the box it was set on.
	if (!input.dataset.nodeFilterBound) {
		input.dataset.nodeFilterBound = "true";
		input.addEventListener("input", () => applyFilter(input, rows, status));
	}
	// Back-navigation and a browser restoring a form value both land here with text already typed, so
	// the list must agree with the box on the way in rather than after the next keystroke.
	applyFilter(input, rows, status);
}

// THREE ways in, and all three are load-bearing.
//
// The first is the one that was missing, and it is why the filter was dead on every node-context page
// reached by an in-app link (Gui, 2026-08-07). Arriving by Turbo, this script is not on the page yet:
// Turbo merges the new head, which starts the module FETCHING, then dispatches `turbo:load` and
// finishes the visit. The module evaluates after that, so both events it was listening for have
// already been and gone and it binds to nothing. Running immediately when the document is already
// parsed is what closes that race; the `readyState` check is what keeps it from running twice on a
// cold load, where the module does evaluate before DOMContentLoaded.
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
// And still `turbo:load`, for every LATER visit: once the script is in the head Turbo leaves it
// alone, so the event is the only signal that a fresh list of rows has been swapped in.
document.addEventListener("turbo:load", init);
